import * as React from "react";
import { useTranslations } from "next-intl";
import { Building2, KeyRound, LogOut, Rocket, UserCog } from "lucide-react";
import { Avatar, Badge, cn } from "@chotca/ui";
import type { UserMe } from "@chotca/sdk";

type Props = {
  user: UserMe;
  orgRole: "OWNER" | "HR" | null;
  onEditProfile: () => void;
  onChangePassword: () => void;
  onUpgrade: () => void;
  onOrgSettings?: () => void;
  onLogout: () => void;
  align?: "right" | "bottom";
};

export function AvatarMenu({
  user,
  orgRole,
  onEditProfile,
  onChangePassword,
  onUpgrade,
  onOrgSettings,
  onLogout,
  align = "right",
}: Props) {
  const t = useTranslations("avatarMenu");
  const tNav = useTranslations("appNav");
  const [open, setOpen] = React.useState(false);
  const closeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapRef = React.useRef<HTMLDivElement | null>(null);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 160);
  };

  React.useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const ep = user.employerProfile;
  const displayName = ep?.fullName?.trim() || user.email || "User";
  const roleLabel =
    orgRole === "OWNER"
      ? t("roleOwner")
      : orgRole === "HR"
        ? t("roleHr")
        : null;

  const panelPosition =
    align === "bottom" ? "top-full mt-3 right-0" : "left-full bottom-0 ml-3";

  return (
    <div
      ref={wrapRef}
      className="relative"
      onMouseEnter={() => {
        cancelClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("trigger")}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex h-11 w-11 items-center justify-center rounded-full transition-all",
          "ring-2 ring-transparent hover:ring-smoke focus-visible:outline-none focus-visible:ring-ember/40",
          open && "ring-ink",
        )}
      >
        <Avatar
          name={displayName}
          src={ep?.avatarUrl ?? undefined}
          size="md"
          tone="ink"
        />
      </button>

      {open ? (
        <div
          role="menu"
          className={cn(
            "absolute z-50 w-72 rounded-[20px] border border-smoke bg-paper p-2 shadow-[0_20px_60px_rgba(15,15,15,0.18)]",
            "animate-in fade-in zoom-in-95 duration-150",
            panelPosition,
          )}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          <div className="flex items-center gap-3 rounded-2xl bg-mist/60 p-3">
            <Avatar
              name={displayName}
              src={ep?.avatarUrl ?? undefined}
              size="lg"
              tone="ink"
            />
            <div className="min-w-0 flex-1">
              <p className="truncate text-title-sm font-extrabold text-ink">
                {displayName}
              </p>
              {user.email ? (
                <p className="truncate text-xs text-ash">{user.email}</p>
              ) : null}
              {roleLabel ? (
                <div className="mt-1.5">
                  <Badge tone={orgRole === "OWNER" ? "lime-soft" : "mist"}>
                    {roleLabel}
                  </Badge>
                </div>
              ) : null}
            </div>
          </div>

          <div className="mt-1.5 flex flex-col">
            <MenuItem
              icon={<Rocket size={18} strokeWidth={1.7} />}
              label="Nâng cấp gói"
              onClick={() => {
                setOpen(false);
                onUpgrade();
              }}
            />
            {onOrgSettings ? (
              <MenuItem
                icon={<Building2 size={18} strokeWidth={1.7} />}
                label={tNav("org")}
                onClick={() => {
                  setOpen(false);
                  onOrgSettings();
                }}
              />
            ) : null}
            <MenuItem
              icon={<UserCog size={18} strokeWidth={1.7} />}
              label={t("editProfile")}
              onClick={() => {
                setOpen(false);
                onEditProfile();
              }}
            />
            <MenuItem
              icon={<KeyRound size={18} strokeWidth={1.7} />}
              label={t("changePassword")}
              onClick={() => {
                setOpen(false);
                onChangePassword();
              }}
            />
            <div className="my-1 h-px bg-smoke" />
            <MenuItem
              icon={<LogOut size={18} strokeWidth={1.7} />}
              label={tNav("logout")}
              tone="danger"
              onClick={() => {
                setOpen(false);
                onLogout();
              }}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  tone?: "danger";
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold transition-colors",
        tone === "danger"
          ? "text-danger hover:bg-danger/10"
          : "text-ink hover:bg-mist",
      )}
    >
      <span
        className={cn(
          "flex h-8 w-8 items-center justify-center rounded-full",
          tone === "danger" ? "bg-danger/10" : "bg-mist",
        )}
      >
        {icon}
      </span>
      <span className="flex-1 truncate">{label}</span>
    </button>
  );
}
